'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, type ReactNode } from 'react';
import { Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AccessGrantLinkDialog, type AccessGrantLinkDialogProps } from './AccessGrantLinkDialog';

/**
 * Props that get forwarded to the dialog. We strip the open-state pieces
 * because the button manages those itself, but we keep the discriminated
 * union intact so callers get type-checking on `mode` + matching fields.
 */
type ForwardedDialogProps = Omit<Extract<AccessGrantLinkDialogProps, {
  mode: 'pick-target';
}>, 'open' | 'onOpenChange'> | Omit<Extract<AccessGrantLinkDialogProps, {
  mode: 'pick-grant';
}>, 'open' | 'onOpenChange'>;
interface AccessGrantLinkButtonProps {
  /** Props forwarded to the dialog. Same `mode` discriminated union. */
  dialog: ForwardedDialogProps;
  /** Optional custom button. When provided we render the dialog only and
   *  expect the caller to control opening via `onClick`. */
  trigger?: (open: () => void) => ReactNode;
  /** Override the default button label/content. */
  children?: ReactNode;
  /** Variant pass-through. */
  variant?: React.ComponentProps<typeof Button>['variant'];
  size?: React.ComponentProps<typeof Button>['size'];
  className?: string;
  /** Optional aria-label / title for the default icon-only button. */
  title?: string;
}

/**
 * One-shot button that opens an AccessGrantLinkDialog. Use the `trigger`
 * render prop if you want a custom button shape; otherwise this renders a
 * default outline button with a chain icon.
 */
export function AccessGrantLinkButton(t0) {
  const $ = _c(14);
  const {
    dialog,
    trigger,
    children,
    variant: t1,
    size,
    className,
    title: t2
  } = t0;
  const variant = t1 === undefined ? "outline" : t1;
  const title = t2 === undefined ? "Generate access link" : t2;
  const [open, setOpen] = useState(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpen(true);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const openDialog = t3;
  let t4;
  if ($[1] !== dialog || $[2] !== open) {
    t4 = dialog.mode === "pick-target" ? <AccessGrantLinkDialog {...dialog} open={open} onOpenChange={setOpen} /> : <AccessGrantLinkDialog {...dialog} open={open} onOpenChange={setOpen} />;
    $[1] = dialog;
    $[2] = open;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const dialogElement = t4;
  let t5;
  if ($[4] !== children || $[5] !== className || $[6] !== size || $[7] !== title || $[8] !== trigger || $[9] !== variant) {
    t5 = trigger ? trigger(openDialog) : <Button type="button" variant={variant} size={size} className={className} onClick={openDialog} title={title}><Link2 className="h-4 w-4 mr-2" />{children ?? "Access link"}</Button>;
    $[4] = children;
    $[5] = className;
    $[6] = size;
    $[7] = title;
    $[8] = trigger;
    $[9] = variant;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== dialogElement || $[12] !== t5) {
    t6 = <>{t5}{dialogElement}</>;
    $[11] = dialogElement;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}